// Libs
import { useCallback, useContext, useEffect, useMemo, useRef, useState, VFC } from 'react'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'

// Context
import GlobalContext from '../../../common/context/GlobalContext'

// Utils
import { getVarValue } from '../../../common/utils/Sparky'

// Components
import {
  TableProvider,
  ConnectedTable,
  TableLayoutContext,
  ColumnOptions,
  ConnectedTableFilter,
  TableInstance,
  useSelectedRowData,
  TableState
} from '@panwds/react-table'
import {
  Body,
  Button,
  Checkbox,
  useToast
} from '@panwds/react-ui'
import {
  SubmitButton
} from '@panwds/react-form'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import TableHeader from '../../../common/components/table/TableHeader'
import IpCellRenderer from './cellRenderers/IpCellRenderer'
import IpActionsRenderer from './cellRenderers/IpActionsRenderer'
import { useModal } from '../../../common/components/modal/Modal'
import PanIpModal from '../modal/PanIpModal'
import IpInheritedRenderer from './cellRenderers/IpInheritedRenderer'

// Constants
import {
  TITLE_TRUSTED_IPS,
  LABEL_LAST_UPDATED_BY,
  HEADER_IP_ADDRESS,
  HEADER_INHERITED,
  LABEL_ALLOW_PAN_IPS,
  LABEL_TRUSTED_IPS_DELETED,
  TOOLTIP_MAX_IPS_REACHED
} from '../../constants/LabelConstants'
import {
  BUTTON_ADD_NEW,
  BUTTON_DELETE,
  BUTTON_SAVE,
  BUTTON_CANCEL,
  LABEL_HEADER_ACTIONS
} from '../../../iam/constants/LabelConstants'
import type { IAM_AllowedIp } from '../../../common/reducers/types/IAMReducer.types'
import IpDeleteModal from '../modal/IpDeleteModal'
import StringTemplate from '../../../common/utils/StringTemplate'
import {
  FF_ALLOW_PANW_IPS
} from '../../../common/utils/FeatureFlags'
import useReadOnly from '../../../common/hooks/useReadOnly'
import { IP_ALLOW_PERMISSION_SET } from '../../../common/constants/GlobalConstants'
import ReadOnlyButton from '../../../common/components/noAccess/ReadOnlyButton'
import TooltipButton from '../../../common/components/tooltipButton/TooltipButton'

const usePanwIps = getVarValue(FF_ALLOW_PANW_IPS, false)

const CONTAINER = styled(V5TableWrapper)`
  input[type="checkbox"] {
    &:disabled {
      cursor: not-allowed !important;
    }
  }
`

export type TrustedIpTableRow = {
  id: string,
  ip: string,
  principal?: string,
  inherited_from?: string,
  used_ips: string[],
  original_id?: string,
  original_used_ips?: string[],
  original_ip?: string
}

const TrustedIpTable: VFC<any> = ({ getAllowedIps, loading }) => {

  const tableInstanceRef = useRef<TableInstance<TrustedIpTableRow>>(null)

  const panIpModal = useModal(PanIpModal)
  const deleteModal = useModal(IpDeleteModal)
  const { toast } = useToast()

  const { treeReducer, iamReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode, sgKeyBy } = treeReducer
  const { allowedIps: _allowedIps, trustedIPLimit } = iamReducer
  const {
    formState,
    getValues,
    unregister,
    setValue,
    reset,
  } = useFormContext()
  const formValues = getValues()
  const { isValid } = formState

  const [data, setData] = useState<TrustedIpTableRow[]>([])
  const [allowPanIps, setAllowPanIps] = useState(false)

  const allowedIps = useMemo(() => {
    return _allowedIps.map(ipData => {
      const { ip, inherited_from, principal } = ipData
      const inherited = inherited_from === selectedTreeNode?.id ? inherited_from : (sgKeyBy[inherited_from]?.display_name || 'N/A')
      return {
        ip,
        principal,
        inherited_from: inherited
      }
    })
  }, [_allowedIps, selectedTreeNode?.id, sgKeyBy])

  const lastUpdatedBy = allowedIps?.[0]?.principal

  const readOnly = useReadOnly(selectedTreeNode?.id, IP_ALLOW_PERMISSION_SET)

  const updateData = useCallback((shouldSet = true) => {
    const table = tableInstanceRef.current
    const formValues = getValues()
    const used_ips = Object.entries(formValues).filter(([key]) => !key.includes('inherited')).map(([, ip]) => ip.replace('/32', ''))
    if (table) {
      data.forEach((item, index) => {
        const row = table.rowsById[index]?.original
        if (row?.ip === 'new') {
          item.ip = _.get(formValues, row.id)
          if (item.id.includes('new')) {
            unregister(item.id)

            if (item.ip === item.original_ip) {
              item.ip = _.cloneDeep(item.original_ip)
              item.id = _.cloneDeep(item.original_id) as string
              item.used_ips = _.cloneDeep(item.original_used_ips) as string[]
            }
            else {
              item.used_ips = used_ips
              item.inherited_from = selectedTreeNode?.id
            }
            delete item.original_id
            delete item.original_ip
            delete item.original_used_ips
            setValue(item.id, item.ip)
          }
        }
      })
    }
    if (shouldSet) {
      setData([...data])
    }
  }, [getValues, data, unregister, setValue, selectedTreeNode?.id])

  const borderClasses = useMemo(() => [
    'cm-border',
    'cm-border-solid',
    'cm-border-gray-300',
    'dark:cm-border-gray-950'
  ], [])

  const classNames = useMemo(() => {
    return [
      'cm-flex-grow',
      'cm-overflow-auto',
      ...borderClasses
    ].join(' ')
  }, [borderClasses])

  const setApiData = useCallback((allowedIps: IAM_AllowedIp[]) => {
    reset()
    allowedIps.sort((ip_a, ip_b) => {
      if (ip_a.inherited_from === selectedTreeNode?.id && ip_b.inherited_from === selectedTreeNode?.id) {
        return ip_a.ip.localeCompare(ip_b.ip)
      }
      if (ip_a.inherited_from === selectedTreeNode?.id) {
        return -1
      }
      if (ip_b.inherited_from === selectedTreeNode?.id) {
        return 1
      }
      return ip_a.ip.localeCompare(ip_b.ip)
    })
    const used_ips = allowedIps.filter(item => item.inherited_from === selectedTreeNode?.id).map(item => item.ip.replace('/32', ''))
    const api_data = allowedIps.map((ipData: IAM_AllowedIp, index) => {
      const { ip, inherited_from, principal } = ipData
      const key = inherited_from === selectedTreeNode?.id ? 'old' : 'inherited'
      const id = `${key}:${inherited_from}:${allowedIps.length - (index + 1)}`
      setValue(id, ip, { shouldDirty: true })
      return {
        id,
        ip,
        principal,
        inherited_from,
        used_ips
      }
    })
    setData(api_data)
  }, [reset, selectedTreeNode?.id, setValue])

  useEffect(() => {
    if (!loading && data.length === 0) {
      setData([
        {
          id: 'any',
          ip: 'Any (Default)',
          used_ips: []
        }
      ])
    }
  }, [loading, data])

  useEffect(() => {
    if (Array.isArray(allowedIps)) {
      setApiData(allowedIps)
    }
    else {
      setApiData([])
    }
  }, [allowedIps, setApiData])

  const deleteModalCallback = useCallback(async (length) => {
    const message = StringTemplate(LABEL_TRUSTED_IPS_DELETED, [length]) as string
    toast(message, { appearance: 'success' })
    if (getAllowedIps) {
      await getAllowedIps(selectedTreeNode?.id, dispatch)
    }
  }, [dispatch, getAllowedIps, selectedTreeNode?.id, toast])

  const onDeleteButtonClick = useCallback((selectedRowData) => {
    deleteModal.openModal({ selected: selectedRowData, callback: deleteModalCallback } as any)
  }, [deleteModal, deleteModalCallback])

  const onDeleteClick = useCallback((row) => {
    // Remove selected items
    if (row.id.includes('old')) {
      onDeleteButtonClick([row])
    }
    else {
      const findIndex = data.findIndex(item => item.id === row.id)
      if (findIndex >= 0) {
        data.splice(findIndex, 1)
        unregister(row.id)
      }
      // Re-index data
      const formItems: any = {}
      data.forEach((item, index) => {
        const formValue = getValues(item.id)
        if (formValue) {
          unregister(item.id)

          if (index < findIndex) {
            const keys = item.id.split(':')
            const index = parseInt(keys.pop() as string, 10)
            item.id = [...keys, index - 1].join(':')
          }
          formItems[item.id] = formValue
          if (item.id.includes('new')) {
            item.ip = formValue
            item.inherited_from = selectedTreeNode?.id
          }
        }
      })
      // Update form state
      for (const [key, value] of Object.entries(formItems)) {
        setValue(key, value)
      }

      setData([...data])
    }
  }, [data, onDeleteButtonClick, unregister, getValues, selectedTreeNode?.id, setValue])

  const onAddClick = useCallback(() => {
    const used_ips = Object.entries(formValues).filter(([key]) => !key.includes('inherited')).map(([, ip]) => ip.replace('/32', ''))

    if (data[0]?.id === 'any') {
      data.shift()
    }
    updateData(false)

    data.unshift({
      id: `new:${data.length}`,
      ip: 'new',
      used_ips
    })

    setData([...data])
  }, [data, formValues, updateData])

  const submitDisabled = useMemo(() => {
    if (readOnly) {
      return true
    }
    return !isValid && data.some(item => item.ip === 'new')
  }, [readOnly, isValid, data])

  const showSubmitButton = useMemo(() => {
    if (readOnly) {
      return false
    }
    if (Object.keys(formValues).length) {
      return Object.keys(formValues).some(key => key.includes('new'))
    }
    return data.some(item => item.ip === 'new')
  }, [data, formValues, readOnly])

  const onCancel = useCallback(() => {
    // const newIp = data.find(item => item.ip === 'new')
    // if (newIp) {
    //   const index = data.findIndex(item => item.ip === 'new')
    //   if (index >= 0) {
    //     const item = data[index]
    //     unregister(item.id)
    //     if (newIp.original_id) {
    //       const {
    //         original_id,
    //         original_used_ips,
    //         original_ip,
    //         principal,
    //         inherited_from
    //       } = item
    //       data[index] = {
    //         id: original_id as string,
    //         used_ips: original_used_ips as string[],
    //         ip: original_ip as string,
    //         principal,
    //         inherited_from
    //       }
    //     }
    //     else {
    //       data.splice(index, 1)
    //     }
    //   }
    //   setData([...data])
    // }
    // else {
    for (const key of Object.keys(formValues)) {
      if (key.includes('new')) {
        unregister(key)
      }
    }
    setApiData(allowedIps)
    // }
  }, [unregister, setApiData, allowedIps, formValues])

  const RightControls = () => {
    const selectedRowData = useSelectedRowData()

    const deleteDisabled = useMemo(() => {
      if (readOnly) {
        return true
      }
      return !selectedRowData.length
    }, [selectedRowData])

    return (
      <div className='cm-flex cm-flex-row cm-gap-2'>
        <ConnectedTableFilter/>
        <ReadOnlyButton
          tsg_id={selectedTreeNode?.id}
          permissionSet={IP_ALLOW_PERMISSION_SET}
          onClick={() => onDeleteButtonClick(selectedRowData)}
          disabled={deleteDisabled}
        >
          {BUTTON_DELETE}
        </ReadOnlyButton>
        {data?.length >= trustedIPLimit ? (
          <TooltipButton
            label={TOOLTIP_MAX_IPS_REACHED}
            disabled
            buttonProps={{
              appearance: 'primary'
            }}
          >{BUTTON_ADD_NEW}</TooltipButton>
        ) :
          (
            <ReadOnlyButton
              tsg_id={selectedTreeNode?.id}
              permissionSet={IP_ALLOW_PERMISSION_SET}
              appearance="primary"
              onClick={onAddClick}
              disabled={submitDisabled}
            >
              {BUTTON_ADD_NEW}
            </ReadOnlyButton>
          )
        }
      </div>
    )
  }

  const handleToggleRowSelected = useCallback((newState: TableState<TrustedIpTableRow>, prevState: TableState<TrustedIpTableRow>) => {
    const selectedRowIds = _.get(newState, 'selectedRowIds', {}) as Record<string, boolean>
    const table = tableInstanceRef.current
    if (table) {
      for (const [rowId, isSelected] of Object.entries(selectedRowIds)) {
        if (isSelected) {
          const row = table.rowsById[rowId]
          const original = row.original
          if (original.id === 'any' || original.ip === 'new' || original.inherited_from !== selectedTreeNode?.id) {
            delete selectedRowIds[rowId]
          }
        }
      }
    }
    return newState
  }, [selectedTreeNode?.id, tableInstanceRef])

  const columns: ColumnOptions<TrustedIpTableRow>[] = useMemo(() => {
    const columns: ColumnOptions<TrustedIpTableRow>[] = [
      {
        id: 'ip',
        Header: HEADER_IP_ADDRESS,
        accessor: 'ip',
        Cell: (props) => <IpCellRenderer {...{ updateData, ...props }}/>,
        width: 400
      },
      {
        id: 'actions',
        Header: LABEL_HEADER_ACTIONS,
        Cell: (props) => (
          <IpActionsRenderer
            {...props}
            onDelete={onDeleteClick}
            hasUnsavedChanges={showSubmitButton}
          />),
        sticky: true,
        stickyPosition: 'right',
        width: 120
      }
    ]
    if (!selectedTreeNode?.isRoot) {
      columns.push({
        id: 'inherited_from',
        Header: HEADER_INHERITED,
        accessor: 'inherited_from',
        width: 250,
        Cell: IpInheritedRenderer
      })
    }
    return columns
  }, [onDeleteClick, selectedTreeNode?.isRoot, showSubmitButton, updateData])

  const customHook = (hooks: any) => {
    hooks.prepareRow.push((row: any) => {
      const { original } = row
      if (original.id === 'any' || original.ip === 'new' || (original.inherited_from && original.inherited_from !== selectedTreeNode?.id)) {
        const originalFn = row.getToggleRowSelectedProps
        row.getToggleRowSelectedProps = () => ({
          disabled: true,
          ...(originalFn && originalFn())
        })
      }
    })
  }

  const overrideHooks = [
    customHook
  ]

  return (
    <CONTAINER className='cm-w-full cm-p-3'>
      <TableProvider
        isLoading={loading}
        {...{ tableInstanceRef, data, columns, overrideHooks }}
        enableRowSelect={!readOnly}
        enableColumnOrder
        enableResizeColumns
        enableColumnSort
        stateReducer={(newState, action, previousState, instance) => {
          switch (action.type) {
            case 'toggleRowSelected':
              return handleToggleRowSelected(newState as TableState<TrustedIpTableRow>, previousState as TableState<TrustedIpTableRow>)
            default:
              return newState
          }
        }}
      >
        <TableHeader
          title={TITLE_TRUSTED_IPS}
          subtitle={lastUpdatedBy ? StringTemplate(LABEL_LAST_UPDATED_BY, [lastUpdatedBy]) as string : ''}
        >
          <RightControls/>
        </TableHeader>
        <div className={classNames}>
          <TableLayoutContext.Provider value={true}>
            <ConnectedTable
              showFooter={false}
            />
          </TableLayoutContext.Provider>
        </div>
      </TableProvider>
      <div className='cm-flex cm-justify-between cm-pt-3'>
        {usePanwIps ? (
          <div className='cm-flex cm-gap-2 cm-items-center'>
            <Checkbox
              disabled={readOnly}
              checked={allowPanIps}
              onClick={() => {
                if (!allowPanIps) {
                  panIpModal.openModal(({ callback: setAllowPanIps }) as any)
                }
                else {
                  setAllowPanIps(false)
                }
              }}
            />
            <Body
              size='sm'
            >
              {LABEL_ALLOW_PAN_IPS}
            </Body>
          </div>
        ) : <div/>}
        {showSubmitButton && (
          <div className='cm-flex cm-gap-2'>
            <Button
              onClick={onCancel}
              disabled={readOnly || !data.some(item => item.id.includes('new'))}
            >
              {BUTTON_CANCEL}
            </Button>
            <SubmitButton
              disabled={submitDisabled}
            >
              {BUTTON_SAVE}
            </SubmitButton>
          </div>
        )}
      </div>
    </CONTAINER>
  )
}

export default TrustedIpTable
